import { useTranslation } from "react-i18next";
import React, { ReactElement } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { cn } from "@/utils/utils";
import Text from "@/components/ui/text";
import { Info } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import DrillDownButton from "@/components/ui/DrillDownButton";
import Spinner from "@/components/ui/spinner";
import FeedbackButton from "@/components/controls/FeedbackButton";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent
} from "@/components/ui/tooltip";

interface Props {
  title: string;
  tabs?: JSX.Element;
  filter?: JSX.Element;
  cardHeight?: string;
  isLoading?: boolean;
  children: ReactElement;
  showFeedBackButton?: boolean;
  feedBackButtonIdContext?: string;
  infoTooltip?: string;
  drillDownOnClick?: () => void;
  isDrillDownHidden?: boolean;
  customOptions?: JSX.Element;
  cardClassName?: string;
  contentClassName?: string;
}

const WidgetCard: React.FC<Props> = ({
  title,
  tabs,
  filter,
  cardHeight,
  isLoading,
  children,
  showFeedBackButton = true,
  feedBackButtonIdContext,
  infoTooltip,
  drillDownOnClick,
  isDrillDownHidden = false,
  customOptions,
  cardClassName,
  contentClassName
}) => {
  const { t } = useTranslation();
  return (
    <Card
      data-test="widget-card"
      className={cn(
        "w-full rounded-xl shadow-none items-center border-border-border1 border-[0.5px] py-5 flex flex-col justify-between overflow-hidden",
        !cardHeight && "h-widget-card",
        "transition-[height] duration-300 ease-in-out",
        cardClassName
      )}
      style={{ height: cardHeight ? cardHeight : "auto" }}
    >
      <CardHeader
        data-test="widget-card-header"
        className="w-full flex flex-row items-center space-y-0 p-0"
      >
        <Text as="div" variant="lgSubtitleH1" className="flex-1">
          {title}
        </Text>
        {tabs && (
          <div className="flex-1 h-full flex items-center justify-center">
            {tabs}
          </div>
        )}
        <div className="flex-1 h-full flex items-center justify-end gap-4">
          {filter}
          <div className="flex items-center gap-1">
            {!isDrillDownHidden && drillDownOnClick && (
              <DrillDownButton
                tooltip={t("drilldown")}
                disabledTooltip={t("drilldownDisabled")}
                onClick={drillDownOnClick}
              />
            )}
            {customOptions && customOptions}
            {showFeedBackButton && (
              <FeedbackButton
                tooltip={t("feedback")}
                idContext={feedBackButtonIdContext}
                iconColor="secondary"
              />
            )}
            {infoTooltip && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <IconContainer color="secondary" size="lg">
                      <Info />
                    </IconContainer>
                  </div>
                </TooltipTrigger>
                <TooltipContent maxWidth={24}>{infoTooltip}</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent
        data-test="widget-card-content"
        className={cn(
          "flex flex-nowrap content-stretch gap-x-10 p-6 h-90 w-full",
          contentClassName
        )}
      >
        {isLoading ? (
          <div className="flex w-full h-full items-center justify-center">
            <Spinner size="lg" />
          </div>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
};
export default WidgetCard;
